import { ATTRIBUTE_OBJECTS, consoleRoutes, WORKFLOW_LIMITS, type AttributeObject } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Input, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { attributesOf, type AttributeDefinition } from "../../../workspace/shared";
import type { SetAttributeStep } from "../types";
import { useRefs } from "./refs";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";

function ValueField({ step, definition, disabled, onChange }: FormProps<SetAttributeStep> & { definition: AttributeDefinition | undefined }) {
  const { t } = useTranslation();
  const set = (value: string) => { onChange({ ...step, value }); };
  if (definition?.type === "bool") {
    return (
      <Field label={t(`${F}.attributeValue`)}>
        <Select value={step.value} disabled={disabled} onChange={(e) => { set(e.target.value); }}>
          <option value="">{t(`${F}.choose`)}</option>
          <option value="true">{t("engage.workflows.conditions.yes")}</option>
          <option value="false">{t("engage.workflows.conditions.no")}</option>
        </Select>
      </Field>
    );
  }
  if (definition?.type === "list" && definition.options) {
    return (
      <Field label={t(`${F}.attributeValue`)}>
        <Select value={step.value} disabled={disabled} onChange={(e) => { set(e.target.value); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {definition.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </Select>
      </Field>
    );
  }
  return (
    <div>
      <Field label={t(`${F}.attributeValue`)} hint={t(`${F}.attributeValueHint`)}>
        <Input maxLength={WORKFLOW_LIMITS.attributeValue} inputMode={definition?.type === "number" ? "decimal" : undefined} disabled={disabled} value={step.value} onChange={(e) => { set(e.target.value); }} />
      </Field>
      <VariableList />
    </div>
  );
}

/** Set attribute: which object, which defined attribute, and the value (a literal or a template). */
export function SetAttributeForm({ step, disabled, onChange }: FormProps<SetAttributeStep>) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { attributes: definitions } = useRefs();
  const options = attributesOf(definitions, step.object);
  const definition = options.find((option) => option.key === step.key);
  return (
    <div className="space-y-4">
      <Field label={t(`${F}.attributeObject`)}>
        <Select value={step.object} disabled={disabled} onChange={(e) => { onChange({ ...step, object: e.target.value as AttributeObject, key: "", value: "" }); }}>
          {ATTRIBUTE_OBJECTS.map((object) => <option key={object} value={object}>{t(`settings.attributes.objects.${object}`)}</option>)}
        </Select>
      </Field>
      <Field label={t(`${F}.attributeKey`)}>
        <Select value={step.key} disabled={disabled || options.length === 0} onChange={(e) => { onChange({ ...step, key: e.target.value, value: "" }); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {step.key && !definition ? <option value={step.key}>{step.key}</option> : null}
          {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
        </Select>
      </Field>
      {options.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="attributes-empty">
          {t(`${F}.noAttributes`)}{" "}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "attributes")}>{t(`${F}.openAttributes`)}</AppLink>
        </p>
      ) : null}
      <ValueField step={step} definition={definition} disabled={disabled} onChange={onChange} />
    </div>
  );
}
